import type { AttackDefinition } from '../../shared/game/combat-types.js';
import { reachRangeToMeters, STANDARD_MELEE_REACH } from '../../shared/game/combat-distances.js';

// Reach/Range 1 requires base contact, 2 allows a 1.5 m gap, and so on.
// Character attributes supply the stat bonus at resolution time. Properties follow the Core Rules weapon tables (pp. 34 to 35): Light weapons can
// be dual wielded (see `shared/game/dual-wield.ts`); the longsword is carried one-handed (the book's "1-handed: Req. 2 STR"), the spear with both hands.
// Thrown melee weapons (dagger, hand axe) are not thrown: a ranged attack is its own weapon here.
export const longsword: AttackDefinition = {
  id: 'longsword', name: 'Longsword', mode: 'melee', count: 1, sides: 8,
  stat: 'strength', bonus: 0, damageType: 'slashing', range: STANDARD_MELEE_REACH, canCrit: true,
};
export const shortsword: AttackDefinition = {
  id: 'shortsword', name: 'Shortsword', mode: 'melee', count: 1, sides: 6,
  stat: 'strength', eitherStat: 'dexterity', bonus: 0, damageType: 'piercing', range: STANDARD_MELEE_REACH, canCrit: true, light: true,
};
export const shortbow: AttackDefinition = {
  id: 'shortbow', name: 'Shortbow', mode: 'ranged', count: 1, sides: 6,
  stat: 'dexterity', bonus: 0, damageType: 'piercing', range: reachRangeToMeters(12), canCrit: true, twoHanded: true,
};
// Core Rules p. 34. Ammunition is not tracked ("if you have a quiver, you have enough arrows"), so javelins are a ranged weapon.
export const dagger: AttackDefinition = {
  id: 'dagger', name: 'Dagger', mode: 'melee', count: 1, sides: 4,
  stat: 'dexterity', bonus: 0, damageType: 'piercing', range: STANDARD_MELEE_REACH, canCrit: true, light: true,
};
export const javelins: AttackDefinition = {
  id: 'javelins', name: 'Javelins', mode: 'ranged', count: 1, sides: 6,
  stat: 'strength', bonus: 0, damageType: 'piercing', range: reachRangeToMeters(8), canCrit: true, stack: 4,
};
// Core Rules p. 34: 2-handed, Range 12, Vicious. Stones are not tracked, like arrows.
export const sling: AttackDefinition = {
  id: 'sling', name: 'Sling', mode: 'ranged', count: 1, sides: 4,
  stat: 'dexterity', bonus: 0, damageType: 'bludgeoning', range: reachRangeToMeters(12), canCrit: true, vicious: true,
};


// Melee, Core Rules p. 34. The club and the mace are the book's one "Club/Mace" entry, drawn as two weapons with the same rules.
export const sickle: AttackDefinition = {
  id: 'sickle', name: 'Sickle', mode: 'melee', count: 1, sides: 4,
  stat: 'dexterity', bonus: 0, damageType: 'slashing', range: STANDARD_MELEE_REACH, canCrit: true, vicious: true,
};
export const club: AttackDefinition = {
  id: 'club', name: 'Club', mode: 'melee', count: 1, sides: 6,
  stat: 'strength', bonus: 0, damageType: 'bludgeoning', range: STANDARD_MELEE_REACH, canCrit: true,
};
export const mace: AttackDefinition = {
  id: 'mace', name: 'Mace', mode: 'melee', count: 1, sides: 6,
  stat: 'strength', bonus: 0, damageType: 'bludgeoning', range: STANDARD_MELEE_REACH, canCrit: true,
};
export const handAxe: AttackDefinition = {
  id: 'hand-axe', name: 'Hand Axe', mode: 'melee', count: 1, sides: 6,
  stat: 'strength', bonus: 0, damageType: 'slashing', range: STANDARD_MELEE_REACH, canCrit: true,
};
export const spear: AttackDefinition = {
  id: 'spear', name: 'Spear', mode: 'melee', count: 1, sides: 6,
  stat: 'strength', bonus: 0, damageType: 'piercing', range: reachRangeToMeters(2), canCrit: true, twoHanded: true,
};
export const rapier: AttackDefinition = {
  id: 'rapier', name: 'Rapier', mode: 'melee', count: 2, sides: 4,
  stat: 'dexterity', bonus: 0, damageType: 'piercing', range: STANDARD_MELEE_REACH, canCrit: true,
};
export const staff: AttackDefinition = {
  id: 'staff', name: 'Staff', mode: 'melee', count: 1, sides: 8,
  stat: 'strength', bonus: 0, damageType: 'bludgeoning', range: STANDARD_MELEE_REACH, canCrit: true, twoHanded: true, weaponType: 'staff',
};
export const battleaxe: AttackDefinition = {
  id: 'battleaxe', name: 'Battleaxe', mode: 'melee', count: 1, sides: 10,
  stat: 'strength', bonus: 0, damageType: 'slashing', range: STANDARD_MELEE_REACH, canCrit: true, twoHanded: true,
};
export const poleHammer: AttackDefinition = {
  id: 'pole-hammer', name: 'Pole Hammer', mode: 'melee', count: 1, sides: 10,
  stat: 'strength', bonus: 0, damageType: 'bludgeoning', range: reachRangeToMeters(2), canCrit: true, twoHanded: true,
};
export const glaive: AttackDefinition = {
  id: 'glaive', name: 'Glaive', mode: 'melee', count: 1, sides: 10,
  stat: 'strength', bonus: 0, damageType: 'slashing', range: reachRangeToMeters(2), canCrit: true, twoHanded: true,
};
export const greatSpear: AttackDefinition = {
  id: 'great-spear', name: 'Great Spear', mode: 'melee', count: 1, sides: 10,
  stat: 'strength', bonus: 0, damageType: 'piercing', range: reachRangeToMeters(2), canCrit: true, twoHanded: true,
};
export const greatmaul: AttackDefinition = {
  id: 'greatmaul', name: 'Greatmaul', mode: 'melee', count: 3, sides: 4,
  stat: 'strength', bonus: 0, damageType: 'bludgeoning', range: STANDARD_MELEE_REACH, canCrit: true, twoHanded: true, strengthRequired: 2,
};
export const greataxe: AttackDefinition = {
  id: 'greataxe', name: 'Greataxe', mode: 'melee', count: 2, sides: 6,
  stat: 'strength', bonus: 0, damageType: 'slashing', range: STANDARD_MELEE_REACH, canCrit: true, twoHanded: true, strengthRequired: 2,
};
// The book lists the greatsword as slashing/piercing; the game records the slash.
export const greatsword: AttackDefinition = {
  id: 'greatsword', name: 'Greatsword', mode: 'melee', count: 1, sides: 12,
  stat: 'strength', bonus: 0, damageType: 'slashing', range: STANDARD_MELEE_REACH, canCrit: true, twoHanded: true, strengthRequired: 2,
};

// Ranged, Core Rules p. 35. Ammunition is not tracked.
export const throwingHammers: AttackDefinition = {
  id: 'throwing-hammers', name: 'Throwing Hammers', mode: 'ranged', count: 1, sides: 8,
  stat: 'strength', bonus: 0, damageType: 'bludgeoning', range: reachRangeToMeters(4), canCrit: true, stack: 3,
};
export const longbow: AttackDefinition = {
  id: 'longbow', name: 'Longbow', mode: 'ranged', count: 1, sides: 8,
  stat: 'dexterity', bonus: 0, damageType: 'piercing', range: reachRangeToMeters(16), canCrit: true, twoHanded: true, strengthRequired: 1,
};
export const crossbow: AttackDefinition = {
  id: 'crossbow', name: 'Crossbow', mode: 'ranged', count: 4, sides: 4,
  stat: 'dexterity', bonus: 0, damageType: 'piercing', range: reachRangeToMeters(8), canCrit: true, twoHanded: true, load: 1,
};
export const handheldBallista: AttackDefinition = {
  id: 'handheld-ballista', name: 'Handheld Ballista', mode: 'ranged', count: 1, sides: 20,
  stat: 'dexterity', bonus: 0, damageType: 'piercing', range: reachRangeToMeters(8), canCrit: true, twoHanded: true, load: 2, strengthRequired: 2,
};

/** Every weapon of the Core Rules tables, in the book's order (melee, then ranged). Cost in gold. */
export const coreWeapons: { weapon: AttackDefinition; cost: number }[] = [
  { weapon: dagger, cost: 3 }, { weapon: sickle, cost: 10 }, { weapon: club, cost: 2 }, { weapon: mace, cost: 2 }, { weapon: handAxe, cost: 8 }, { weapon: spear, cost: 10 },
  { weapon: shortsword, cost: 15 }, { weapon: rapier, cost: 60 }, { weapon: staff, cost: 8 }, { weapon: longsword, cost: 60 }, { weapon: battleaxe, cost: 30 },
  { weapon: poleHammer, cost: 60 }, { weapon: glaive, cost: 60 }, { weapon: greatSpear, cost: 60 }, { weapon: greatmaul, cost: 80 }, { weapon: greataxe, cost: 100 },
  { weapon: greatsword, cost: 120 }, { weapon: sling, cost: 4 }, { weapon: javelins, cost: 20 }, { weapon: throwingHammers, cost: 25 }, { weapon: shortbow, cost: 25 },
  { weapon: longbow, cost: 30 }, { weapon: crossbow, cost: 60 }, { weapon: handheldBallista, cost: 120 },
];
